import React, { ForwardedRef, forwardRef, memo, PropsWithChildren, ReactElement, Ref, useState } from 'react';

import { notUndefined, useVirtualizer } from '@tanstack/react-virtual';

import { DataTable } from './DataTable';
import { DataTableInternalProps, DataTableProps } from './types';

export interface VirtualizedDataTableProps<DataType> extends Omit<DataTableProps<DataType>, 'stickyHeader'> {
  /**
   * 테이블 박스 내부 스크롤에 헤더를 고정할지 여부
   *
   * @default true
   */
  stickyHeader?: boolean;
  /**
   * 가상화를 위한 Row의 높이
   */
  estimateRowSize: number;
}

const DataTableInternal = DataTable as <T extends object>(
  props: DataTableInternalProps<T> & { ref?: Ref<HTMLTableElement> },
) => ReactElement;

export const VirtualizedDataTable = memo(
  forwardRef(
    <DataType extends object>(props: VirtualizedDataTableProps<DataType>, ref: ForwardedRef<HTMLTableElement>) => {
      const [tableParentRef, setTableParentRef] = useState<HTMLDivElement | null>(null);
      const stickyHeader = props.stickyHeader;
      if (typeof stickyHeader === 'object' && stickyHeader !== null) {
        throw new Error(
          'VirtualizedDataTable은 page sticky header(`stickyHeader={{ mode: "page" }}`)를 지원하지 않습니다. stickyHeader에 boolean을 사용하세요.',
        );
      }

      const virtualizer = useVirtualizer({
        count: props.rows.length,
        getScrollElement: () => tableParentRef,
        estimateSize: () => props.estimateRowSize,
        overscan: 5,
      });

      const virtualizedItems = virtualizer.getVirtualItems();
      const [beforePadding, afterPadding] =
        virtualizedItems.length > 0
          ? [
              notUndefined(virtualizedItems[0]).start - virtualizer.options.scrollMargin,
              virtualizer.getTotalSize() - notUndefined(virtualizedItems[virtualizedItems.length - 1]).end,
            ]
          : [0, 0];

      return (
        <DataTableInternal
          ref={ref}
          {...props}
          stickyHeader={stickyHeader}
          setContainerRef={setTableParentRef}
          virtualizedProps={{ virtualizedItems, beforePadding, afterPadding }}
        />
      );
    },
  ),
) as <DataType extends object>(
  props: PropsWithChildren<VirtualizedDataTableProps<DataType>> & { ref?: Ref<HTMLTableElement> },
) => ReactElement;
